import i18next from 'i18next'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next } from 'react-i18next'
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage } from './locales'

/**
 * Best-effort initial language from the visitor's browser. Once a booking token
 * loads, the campaign language takes over; this only decides what a candidate sees
 * before that — most importantly on the token-less not-found screen, where there's
 * no campaign context to fall back on.
 *
 * `navigator.languages` is an ordered *preference* list, so we honour it in order:
 * for each candidate we take an exact tag match, else the same primary language
 * (RFC 4647 "lookup"). The first candidate that resolves wins. This is deliberate,
 * not a bug: for `['en-US','fi-FI']` with `en-GB`+`fi-FI` supported we return
 * `en-GB` — the visitor prefers English, and `en-GB` is English; falling through
 * to the exact `fi-FI` would wrongly serve their *second* choice.
 */
function detectInitialLanguage(): SupportedLanguage {
	const candidates = typeof navigator !== 'undefined'
		? (navigator.languages ?? [navigator.language])
		: []

	for (const candidate of candidates) {
		const exact = SUPPORTED_LANGUAGES.find((language) => language.toLowerCase() === candidate.toLowerCase())
		if (exact) {
			return exact
		}
		const primary = candidate.split('-')[0]?.toLowerCase()
		const byPrimary = SUPPORTED_LANGUAGES.find((language) => language.split('-')[0].toLowerCase() === primary)
		if (byPrimary) {
			return byPrimary
		}
	}

	return DEFAULT_LANGUAGE
}

// Each language's `booking` strings are a separate chunk, fetched the first time
// the page asks for that language. A visitor only ever downloads the one locale
// they view, never all seven.
const lazyResources = import.meta.glob<Record<string, unknown>>(
	'./locales/*/booking.json',
	{ import: 'default' },
)

void i18next
	.use(resourcesToBackend((language: string, namespace: string) => {
		const loader = lazyResources[`./locales/${language}/${namespace}.json`]
		if (!loader) {
			return Promise.reject(new Error(`No lazy i18n resource for ${language}/${namespace}`))
		}
		return loader()
	}))
	.use(initReactI18next)
	.init({
		lng: detectInitialLanguage(),
		fallbackLng: DEFAULT_LANGUAGE,
		ns: ['booking'],
		defaultNS: 'booking',
		interpolation: { escapeValue: false },
	})

export default i18next
